/**
 * AdminVoicePracticeScenarios — CRUD page for Voice Practice Scenarios
 * Route: /admin/voice-practice-scenarios
 * Roles: admin, superadmin
 *
 * Schema v2 fields:
 *   tips_tr, tips_en, passing_score, is_dialog_capable, dialog_role,
 *   audio_url, unit_id, order  (in addition to existing fields)
 */
import { useState } from 'react';
import AdminLayout from '../components/admin/AdminLayout';
import { PageHeader, ConfirmDialog, LoadingState } from '../components/admin';
import DataTable from '../components/admin/DataTable';
import BilingualTextField from '../components/admin/BilingualTextField';
import ChipInput from '../components/admin/ChipInput';
import { useLanguage } from '../contexts/LanguageContext';
import { useToast } from '../contexts/ToastContext';
import { getApiErrorMessage } from '../utils/apiError';
import {
  useVoiceScenarios,
  useCreateVoiceScenario,
  useUpdateVoiceScenario,
  useDeleteVoiceScenario,
} from '../api/admin/cabinModules';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Plus, Mic, Edit, Trash2, ChevronDown, ChevronUp, MessageSquare } from 'lucide-react';
import { motion } from 'framer-motion';

// Backend enum: announce | safety | service | emergency | general | turbulence | landing | delay
const CATEGORY_VALUES = [
  'announce',
  'safety',
  'service',
  'emergency',
  'general',
  'turbulence',
  'landing',
  'delay',
];

const LEVELS = [
  { value: 'beginner', labelKey: 'beginner' },
  { value: 'intermediate', labelKey: 'intermediate' },
  { value: 'advanced', labelKey: 'advanced' },
];

// Konusma dili — STT transcribe icin default scenario dili (yeni 2026-05-17)
const SPOKEN_LANGUAGES = [
  { value: 'en', labelTr: 'English (EN)', labelEn: 'English (EN)' },
  { value: 'tr', labelTr: 'Türkçe (TR)', labelEn: 'Turkish (TR)' },
];

const DIALOG_ROLES = [
  { value: '', labelKey: 'admin.voicePractice.dialogRoleNone' },
  { value: 'passenger', labelKey: 'admin.voicePractice.dialogRolePassenger' },
  { value: 'supervisor', labelKey: 'admin.voicePractice.dialogRoleSupervisor' },
  { value: 'interviewer', labelKey: 'admin.voicePractice.dialogRoleInterviewer' },
];

const EMPTY_FORM = {
  id: '',
  category: '',
  level: 'beginner',
  order: 0,
  title_tr: '',
  title_en: '',
  description_tr: '',
  description_en: '',
  tips_tr: [],
  tips_en: [],
  reference_text_tr: '',
  reference_text_en: '',
  target_duration_sec: 60,
  passing_score: 60,
  tags: [],
  is_dialog_capable: false,
  dialog_role: '',
  dialog_opener_tr: '',
  dialog_opener_en: '',
  dialog_context_tr: '',
  dialog_context_en: '',
  dialog_max_turns_override: '',
  audio_url: '',
  unit_id: '',
  spoken_language: 'en',
  is_active: true,
};

/** Simple URL validator — accepts empty string (optional field). */
function isValidUrl(val) {
  if (!val) return true;
  try {
    new URL(val);
    return true;
  } catch {
    return false;
  }
}

export default function AdminVoicePracticeScenarios() {
  const { lang, t } = useLanguage();
  const toast = useToast();

  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({ category: '', level: '', is_active: '' });
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [advancedOpen, setAdvancedOpen] = useState(false);

  const activeFilters = Object.fromEntries(
    Object.entries(filters).filter(([, v]) => v !== '')
  );

  const { data, isLoading } = useVoiceScenarios(page, activeFilters);
  const createMutation = useCreateVoiceScenario();
  const updateMutation = useUpdateVoiceScenario();
  const deleteMutation = useDeleteVoiceScenario();

  const items = data?.items || data?.scenarios || [];
  const total = data?.total || 0;
  const totalPages = data?.pages || Math.ceil(total / 20) || 1;

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setAdvancedOpen(false);
    setModalOpen(true);
  };

  const openEdit = (row) => {
    setEditingId(row.id || row._id);
    setForm({
      id: row.id || row._id || '',
      category: row.category || '',
      level: row.level || 'beginner',
      order: row.order ?? 0,
      title_tr: row.title_tr || '',
      title_en: row.title_en || '',
      description_tr: row.description_tr || '',
      description_en: row.description_en || '',
      tips_tr: row.tips_tr || [],
      tips_en: row.tips_en || [],
      reference_text_tr: row.reference_text_tr || '',
      reference_text_en: row.reference_text_en || '',
      target_duration_sec: row.target_duration_sec || 60,
      passing_score: row.passing_score ?? 60,
      tags: row.tags || [],
      is_dialog_capable: row.is_dialog_capable === true,
      dialog_role: row.dialog_role || '',
      dialog_opener_tr: row.dialog_opener_tr ?? '',
      dialog_opener_en: row.dialog_opener_en ?? '',
      dialog_context_tr: row.dialog_context_tr ?? '',
      dialog_context_en: row.dialog_context_en ?? '',
      dialog_max_turns_override: row.dialog_max_turns_override?.toString() ?? '',
      audio_url: row.audio_url || '',
      unit_id: row.unit_id || '',
      spoken_language: row.spoken_language || 'en',
      is_active: row.is_active !== false,
    });
    // auto-open advanced section if any advanced field is set
    const hasAdvanced =
      row.is_dialog_capable ||
      row.dialog_role ||
      row.dialog_opener_tr ||
      row.dialog_opener_en ||
      row.dialog_context_tr ||
      row.dialog_context_en ||
      row.dialog_max_turns_override != null ||
      row.audio_url ||
      row.unit_id ||
      (row.passing_score !== undefined && row.passing_score !== 60);
    setAdvancedOpen(!!hasAdvanced);
    setModalOpen(true);
  };

  const fieldChange = (name, value) => setForm((f) => ({ ...f, [name]: value }));

  const handleSave = async () => {
    if (!form.title_tr || !form.title_en) {
      toast.error(t('admin.voicePractice.validationTitleRequired'));
      return;
    }
    if (!form.category) {
      toast.error(t('admin.voicePractice.validationCategoryRequired'));
      return;
    }
    const ps = Number(form.passing_score);
    if (isNaN(ps) || ps < 0 || ps > 100) {
      toast.error(t('admin.voicePractice.validationPassingScore'));
      return;
    }
    if (!form.is_dialog_capable && form.dialog_role) {
      toast.error(t('admin.voicePractice.validationDialogRole'));
      return;
    }
    if (form.audio_url && !isValidUrl(form.audio_url)) {
      toast.error(t('admin.voicePractice.validationAudioUrl'));
      return;
    }
    if (form.dialog_opener_tr && form.dialog_opener_tr.length > 500) {
      toast.error(t('admin.voicePractice.validationDialogOpener'));
      return;
    }
    if (form.dialog_opener_en && form.dialog_opener_en.length > 500) {
      toast.error(t('admin.voicePractice.validationDialogOpener'));
      return;
    }
    if (form.dialog_context_tr && form.dialog_context_tr.length > 2000) {
      toast.error(t('admin.voicePractice.validationDialogContext'));
      return;
    }
    if (form.dialog_context_en && form.dialog_context_en.length > 2000) {
      toast.error(t('admin.voicePractice.validationDialogContext'));
      return;
    }
    let dialogMaxTurns = null;
    if (form.dialog_max_turns_override !== '') {
      const parsed = parseInt(form.dialog_max_turns_override, 10);
      if (isNaN(parsed) || parsed < 2 || parsed > 20) {
        toast.error(t('admin.voicePractice.validationDialogMaxTurns'));
        return;
      }
      dialogMaxTurns = parsed;
    }

    // Build clean payload — omit empty optional strings
    const payload = {
      ...form,
      passing_score: ps,
      order: Number(form.order) || 0,
      dialog_role: form.is_dialog_capable ? (form.dialog_role || null) : null,
      dialog_opener_tr: form.is_dialog_capable ? (form.dialog_opener_tr || null) : null,
      dialog_opener_en: form.is_dialog_capable ? (form.dialog_opener_en || null) : null,
      dialog_context_tr: form.is_dialog_capable ? (form.dialog_context_tr || null) : null,
      dialog_context_en: form.is_dialog_capable ? (form.dialog_context_en || null) : null,
      dialog_max_turns_override: form.is_dialog_capable ? dialogMaxTurns : null,
      audio_url: form.audio_url || null,
      unit_id: form.unit_id || null,
    };

    try {
      if (editingId) {
        await updateMutation.mutateAsync({ id: editingId, data: payload });
        toast.success(t('admin.voicePractice.updateSuccess'));
      } else {
        await createMutation.mutateAsync(payload);
        toast.success(t('admin.voicePractice.createSuccess'));
      }
      setModalOpen(false);
    } catch (err) {
      toast.error(getApiErrorMessage(err, lang));
    }
  };

  const handleDeleteClick = (row) => {
    setDeleteTarget(row);
    setConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await deleteMutation.mutateAsync(deleteTarget.id || deleteTarget._id);
      toast.success(t('admin.voicePractice.deleteSuccess'));
    } catch (err) {
      toast.error(getApiErrorMessage(err, lang));
    }
    setConfirmOpen(false);
    setDeleteTarget(null);
  };

  const levelColors = {
    beginner: 'bg-emerald-100 text-emerald-700',
    intermediate: 'bg-amber-100 text-amber-700',
    advanced: 'bg-rose-100 text-rose-700',
  };

  // render signature: (cellValue, fullRow)
  const columns = [
    {
      key: 'order',
      label: t('admin.voicePractice.colOrder'),
      sortable: true,
      render: (val) => (
        <span className="text-xs text-gray-500 font-mono">{val ?? 0}</span>
      ),
    },
    {
      key: 'title_tr',
      label: t('admin.voicePractice.colTitle'),
      sortable: true,
      render: (val) => (
        <span className="font-medium text-gray-800 truncate max-w-[180px] block">
          {val}
        </span>
      ),
    },
    {
      key: 'category',
      label: t('admin.voicePractice.colCategory'),
      render: (val) => (
        <Badge variant="secondary" className="text-xs">
          {t(`admin.voicePractice.category.${val}`) || val}
        </Badge>
      ),
    },
    {
      key: 'level',
      label: t('admin.voicePractice.colLevel'),
      render: (val) => (
        <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${levelColors[val] || ''}`}>
          {t(val) || val}
        </span>
      ),
    },
    {
      key: 'target_duration_sec',
      label: t('admin.voicePractice.colDuration'),
      render: (val) => <span className="text-sm text-gray-600">{val}s</span>,
    },
    {
      key: 'passing_score',
      label: t('admin.voicePractice.colPassingScore'),
      render: (val) => (
        <span className="text-sm font-mono text-gray-700">{val ?? 60}</span>
      ),
    },
    {
      key: 'is_dialog_capable',
      label: t('admin.voicePractice.colDialogCapable'),
      render: (val) =>
        val ? (
          <Badge className="bg-cyan-100 text-cyan-700 flex items-center gap-1 w-fit">
            <MessageSquare size={11} />
            {t('admin.voicePractice.fieldIsDialogCapable')}
          </Badge>
        ) : (
          <span className="text-xs text-gray-400">—</span>
        ),
    },
    {
      key: 'is_active',
      label: t('admin.voicePractice.colActive'),
      render: (val) => (
        <Badge className={val ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'}>
          {val ? t('admin.voicePractice.fieldActive') : t('admin.voicePractice.fieldInactive')}
        </Badge>
      ),
    },
    {
      key: 'actions',
      label: t('admin.voicePractice.colActions'),
      render: (_val, row) => (
        <div className="flex items-center gap-2">
          <Button size="sm" variant="ghost" onClick={() => openEdit(row)} className="h-7 px-2">
            <Edit size={14} className="mr-1" />
            {t('admin.btn_edit')}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => handleDeleteClick(row)}
            className="h-7 px-2 text-rose-500 hover:text-rose-700 hover:bg-rose-50"
          >
            <Trash2 size={14} className="mr-1" />
            {t('admin.btn_delete')}
          </Button>
        </div>
      ),
    },
  ];

  const isSaving = createMutation.isPending || updateMutation.isPending;

  return (
    <AdminLayout>
      <PageHeader
        breadcrumbs={[
          { label: 'Admin', href: '/admin' },
          { label: t('admin.nav.cabinAcademy'), href: '/admin/cabin-academy' },
          { label: t('admin.nav.voicePractice') },
        ]}
        title={t('admin.voicePractice.title')}
        subtitle={t('admin.voicePractice.subtitle')}
        icon={Mic}
        actions={
          <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
            <Button
              onClick={openCreate}
              className="flex items-center gap-2 bg-cyan-500 hover:bg-cyan-600 text-white"
            >
              <Plus size={16} />
              {t('admin.voicePractice.createBtn')}
            </Button>
          </motion.div>
        }
      />

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-4">
        <Select
          value={filters.category}
          onValueChange={(v) => setFilters((f) => ({ ...f, category: v === 'all' ? '' : v }))}
        >
          <SelectTrigger className="w-48 h-9 text-sm">
            <SelectValue placeholder={t('admin.voicePractice.filterCategory')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('admin.filter_all')}</SelectItem>
            {CATEGORY_VALUES.map((cv) => (
              <SelectItem key={cv} value={cv}>
                {t(`admin.voicePractice.category.${cv}`) || cv}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          value={filters.level}
          onValueChange={(v) => setFilters((f) => ({ ...f, level: v === 'all' ? '' : v }))}
        >
          <SelectTrigger className="w-40 h-9 text-sm">
            <SelectValue placeholder={t('admin.voicePractice.filterLevel')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('admin.filter_all')}</SelectItem>
            {LEVELS.map((l) => (
              <SelectItem key={l.value} value={l.value}>
                {t(l.value) || l.value}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          value={filters.is_active}
          onValueChange={(v) => setFilters((f) => ({ ...f, is_active: v === 'all' ? '' : v }))}
        >
          <SelectTrigger className="w-36 h-9 text-sm">
            <SelectValue placeholder={t('admin.voicePractice.filterStatus')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('admin.filter_all')}</SelectItem>
            <SelectItem value="true">{lang === 'tr' ? 'Aktif' : 'Active'}</SelectItem>
            <SelectItem value="false">{lang === 'tr' ? 'Pasif' : 'Inactive'}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {isLoading ? (
        <LoadingState />
      ) : (
        <DataTable
          columns={columns}
          data={items}
          rowKey={(r) => r.id || r._id}
          emptyTitle={t('admin.voicePractice.emptyTitle')}
          emptyDescription={t('admin.voicePractice.emptyDesc')}
        />
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-4">
          <Button
            variant="outline"
            size="sm"
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
          >
            {t('admin.prev_page')}
          </Button>
          <span className="text-sm text-gray-600">
            {page} / {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            {t('admin.next_page')}
          </Button>
        </div>
      )}

      {/* Create / Edit Modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingId
                ? t('admin.voicePractice.editTitle')
                : t('admin.voicePractice.createTitle')}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-5 py-2">
            {/* ── ID (create only) ── */}
            {!editingId && (
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-gray-600">
                  ID (slug) <span className="text-rose-500">*</span>
                </Label>
                <Input
                  value={form.id}
                  onChange={(e) => fieldChange('id', e.target.value)}
                  placeholder="cabin_safety_intro"
                  className="text-sm font-mono"
                />
              </div>
            )}

            {/* ── Category + Level + Order row ── */}
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-gray-600">
                  {t('admin.voicePractice.fieldCategory')} <span className="text-rose-500">*</span>
                </Label>
                <Select value={form.category} onValueChange={(v) => fieldChange('category', v)}>
                  <SelectTrigger className="h-9 text-sm">
                    <SelectValue placeholder={t('admin.select_placeholder')} />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORY_VALUES.map((cv) => (
                      <SelectItem key={cv} value={cv}>
                        {t(`admin.voicePractice.category.${cv}`) || cv}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-gray-600">
                  {t('admin.voicePractice.fieldLevel')}
                </Label>
                <Select value={form.level} onValueChange={(v) => fieldChange('level', v)}>
                  <SelectTrigger className="h-9 text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {LEVELS.map((l) => (
                      <SelectItem key={l.value} value={l.value}>
                        {t(l.value) || l.value}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Konusma Dili — STT transcribe default'u (yeni 2026-05-17) */}
              <div className="space-y-1.5">
                <Label
                  className="text-xs font-semibold text-gray-600"
                  title={lang === 'tr'
                    ? 'Bu senaryoda kullanicinin konusacagi varsayilan dil. Kullanici override edebilir.'
                    : 'Default spoken language for this scenario. User can override on the practice screen.'}
                >
                  {lang === 'tr' ? 'Konusma Dili' : 'Spoken Language'}
                </Label>
                <Select
                  value={form.spoken_language}
                  onValueChange={(v) => fieldChange('spoken_language', v)}
                >
                  <SelectTrigger className="h-9 text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SPOKEN_LANGUAGES.map((sl) => (
                      <SelectItem key={sl.value} value={sl.value}>
                        {lang === 'tr' ? sl.labelTr : sl.labelEn}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label
                  className="text-xs font-semibold text-gray-600"
                  title={t('admin.voicePractice.fieldOrderTooltip')}
                >
                  {t('admin.voicePractice.fieldOrder')}
                </Label>
                <Input
                  type="number"
                  min={0}
                  value={form.order}
                  onChange={(e) => fieldChange('order', parseInt(e.target.value, 10) || 0)}
                  className="h-9 text-sm"
                />
              </div>
            </div>

            {/* ── Title TR/EN ── */}
            <BilingualTextField
              labelTr={t('admin.voicePractice.fieldTitleTr')}
              labelEn={t('admin.voicePractice.fieldTitleEn')}
              nameTr="title_tr"
              nameEn="title_en"
              valueTr={form.title_tr}
              valueEn={form.title_en}
              onChange={fieldChange}
              required
            />

            {/* ── Description TR/EN ── */}
            <BilingualTextField
              labelTr={t('admin.voicePractice.fieldDescTr')}
              labelEn={t('admin.voicePractice.fieldDescEn')}
              nameTr="description_tr"
              nameEn="description_en"
              valueTr={form.description_tr}
              valueEn={form.description_en}
              onChange={fieldChange}
              multiline
              rows={2}
            />

            {/* ── Tips TR / Tips EN ── */}
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-gray-600">
                {t('admin.voicePractice.fieldTipsTr')}
              </Label>
              <ChipInput
                value={form.tips_tr}
                onChange={(chips) => {
                  if (chips.length > 10) {
                    toast.error(t('admin.voicePractice.tipsMaxError'));
                    return;
                  }
                  fieldChange('tips_tr', chips);
                }}
                placeholder={t('admin.voicePractice.tipsTrPlaceholder')}
                max={10}
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-gray-600">
                {t('admin.voicePractice.fieldTipsEn')}
              </Label>
              <ChipInput
                value={form.tips_en}
                onChange={(chips) => {
                  if (chips.length > 10) {
                    toast.error(t('admin.voicePractice.tipsMaxError'));
                    return;
                  }
                  fieldChange('tips_en', chips);
                }}
                placeholder={t('admin.voicePractice.tipsEnPlaceholder')}
                max={10}
              />
            </div>

            {/* ── Reference Text TR/EN ── */}
            <BilingualTextField
              labelTr={t('admin.voicePractice.fieldRefTextTr')}
              labelEn={t('admin.voicePractice.fieldRefTextEn')}
              nameTr="reference_text_tr"
              nameEn="reference_text_en"
              valueTr={form.reference_text_tr}
              valueEn={form.reference_text_en}
              onChange={fieldChange}
              multiline
              rows={4}
            />

            {/* ── Duration ── */}
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-gray-600">
                {t('admin.voicePractice.fieldDuration')}
              </Label>
              <Input
                type="number"
                min={15}
                max={600}
                value={form.target_duration_sec}
                onChange={(e) =>
                  fieldChange('target_duration_sec', parseInt(e.target.value, 10) || 60)
                }
                className="w-32 text-sm"
              />
              <p className="text-xs text-gray-400">{t('admin.voicePractice.fieldDurationHint')}</p>
            </div>

            {/* ── Tags ── */}
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-gray-600">
                {t('admin.voicePractice.fieldTags')}
              </Label>
              <ChipInput
                value={form.tags}
                onChange={(tags) => fieldChange('tags', tags)}
                placeholder={t('admin.voicePractice.tagsPlaceholder')}
              />
            </div>

            {/* ── Active toggle ── */}
            <div className="flex items-center gap-3">
              <Switch
                checked={form.is_active}
                onCheckedChange={(v) => fieldChange('is_active', v)}
                id="vps-is-active"
              />
              <Label htmlFor="vps-is-active" className="text-sm font-medium cursor-pointer">
                {t('admin.voicePractice.fieldActive')}
              </Label>
            </div>

            {/* ── Advanced Settings (collapsible) ── */}
            <div className="border border-gray-200 rounded-xl overflow-hidden">
              <button
                type="button"
                onClick={() => setAdvancedOpen((o) => !o)}
                className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors text-sm font-semibold text-gray-700"
              >
                {t('admin.voicePractice.sectionAdvanced')}
                {advancedOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              </button>

              {advancedOpen && (
                <div className="p-4 space-y-5">
                  {/* Passing Score */}
                  <div className="space-y-1.5">
                    <Label className="text-xs font-semibold text-gray-600">
                      {t('admin.voicePractice.fieldPassingScore')}
                    </Label>
                    <Input
                      type="number"
                      min={0}
                      max={100}
                      value={form.passing_score}
                      onChange={(e) => {
                        const v = parseInt(e.target.value, 10);
                        fieldChange('passing_score', isNaN(v) ? 60 : v);
                      }}
                      className="w-32 text-sm"
                    />
                    <p className="text-xs text-gray-400">
                      {t('admin.voicePractice.fieldPassingScoreHint')}
                    </p>
                  </div>

                  {/* is_dialog_capable */}
                  <div className="flex items-center gap-3">
                    <Switch
                      checked={form.is_dialog_capable}
                      onCheckedChange={(v) => {
                        fieldChange('is_dialog_capable', v);
                        if (!v) {
                          setForm((f) => ({
                            ...f,
                            is_dialog_capable: v,
                            dialog_role: '',
                            dialog_opener_tr: '',
                            dialog_opener_en: '',
                            dialog_context_tr: '',
                            dialog_context_en: '',
                            dialog_max_turns_override: '',
                          }));
                        }
                      }}
                      id="vps-dialog-capable"
                    />
                    <Label
                      htmlFor="vps-dialog-capable"
                      className="text-sm font-medium cursor-pointer"
                    >
                      {t('admin.voicePractice.fieldIsDialogCapable')}
                    </Label>
                  </div>

                  {/* dialog_role — conditional */}
                  {form.is_dialog_capable && (
                    <div className="space-y-1.5">
                      <Label className="text-xs font-semibold text-gray-600">
                        {t('admin.voicePractice.fieldDialogRole')}
                      </Label>
                      <Select
                        value={form.dialog_role || ''}
                        onValueChange={(v) => fieldChange('dialog_role', v)}
                      >
                        <SelectTrigger className="h-9 text-sm w-64">
                          <SelectValue
                            placeholder={t('admin.voicePractice.dialogRoleNone')}
                          />
                        </SelectTrigger>
                        <SelectContent>
                          {DIALOG_ROLES.map((dr) => (
                            <SelectItem key={dr.value} value={dr.value}>
                              {t(dr.labelKey)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {/* Dialog Settings sub-section — conditional on is_dialog_capable */}
                  {form.is_dialog_capable && (
                    <div className="border border-cyan-100 rounded-xl p-4 space-y-4 bg-cyan-50/40">
                      <p className="text-xs font-semibold text-cyan-700 uppercase tracking-wide">
                        {t('admin.voicePractice.sectionDialog')}
                      </p>

                      {/* Dialog Opener TR/EN */}
                      <BilingualTextField
                        labelTr={t('admin.voicePractice.fieldDialogOpenerTr')}
                        labelEn={t('admin.voicePractice.fieldDialogOpenerEn')}
                        nameTr="dialog_opener_tr"
                        nameEn="dialog_opener_en"
                        valueTr={form.dialog_opener_tr}
                        valueEn={form.dialog_opener_en}
                        onChange={fieldChange}
                        multiline
                        rows={2}
                      />
                      <p className="text-xs text-gray-400 -mt-2">
                        {t('admin.voicePractice.fieldDialogOpenerHint')}
                      </p>

                      {/* Dialog Context TR/EN */}
                      <BilingualTextField
                        labelTr={t('admin.voicePractice.fieldDialogContextTr')}
                        labelEn={t('admin.voicePractice.fieldDialogContextEn')}
                        nameTr="dialog_context_tr"
                        nameEn="dialog_context_en"
                        valueTr={form.dialog_context_tr}
                        valueEn={form.dialog_context_en}
                        onChange={fieldChange}
                        multiline
                        rows={4}
                      />
                      <p className="text-xs text-gray-400 -mt-2">
                        {t('admin.voicePractice.fieldDialogContextHint')}
                      </p>

                      {/* Max Turns Override */}
                      <div className="space-y-1.5">
                        <Label className="text-xs font-semibold text-gray-600">
                          {t('admin.voicePractice.fieldDialogMaxTurnsOverride')}
                        </Label>
                        <Input
                          type="number"
                          min={2}
                          max={20}
                          value={form.dialog_max_turns_override}
                          onChange={(e) => fieldChange('dialog_max_turns_override', e.target.value)}
                          placeholder={t('admin.voicePractice.fieldDialogMaxTurnsPlaceholder')}
                          className="w-40 text-sm"
                        />
                        <p className="text-xs text-gray-400">
                          {t('admin.voicePractice.fieldDialogMaxTurnsHint')}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* audio_url */}
                  <div className="space-y-1.5">
                    <Label className="text-xs font-semibold text-gray-600">
                      {t('admin.voicePractice.fieldAudioUrl')}
                    </Label>
                    <Input
                      type="url"
                      value={form.audio_url}
                      onChange={(e) => fieldChange('audio_url', e.target.value)}
                      placeholder="https://..."
                      className="text-sm"
                    />
                    <p className="text-xs text-gray-400">
                      {t('admin.voicePractice.fieldAudioUrlHint')}
                    </p>
                  </div>

                  {/* unit_id */}
                  <div className="space-y-1.5">
                    <Label className="text-xs font-semibold text-gray-600">
                      {t('admin.voicePractice.fieldUnitId')}
                    </Label>
                    <Input
                      type="text"
                      value={form.unit_id}
                      onChange={(e) => fieldChange('unit_id', e.target.value)}
                      placeholder="unit_001"
                      className="text-sm font-mono"
                    />
                    <p className="text-xs text-gray-400">
                      {t('admin.voicePractice.fieldUnitIdHint')}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setModalOpen(false)}>
              {t('admin.btn_cancel')}
            </Button>
            <Button
              onClick={handleSave}
              disabled={isSaving}
              className="bg-cyan-500 hover:bg-cyan-600 text-white"
            >
              {isSaving ? t('admin.btn_loading') : t('admin.btn_save') || 'Kaydet'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirm */}
      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title={t('admin.voicePractice.deleteTitle')}
        description={t('admin.voicePractice.deleteDesc')}
        onConfirm={handleDeleteConfirm}
        loading={deleteMutation.isPending}
      />
    </AdminLayout>
  );
}
